import React, { useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { ArkorbitGridBackground } from '../arkorbit-ui/ArkorbitGridBackground';
import { BRAND_PORTAL } from '../../constants';
import type { CustomerRecord } from '../CustomerLogin';
import { PORTAL_SHELL } from './portalTheme';
import { portalViewTitle, type PortalView } from './portalNavigation';
import { PortalSidebar, type PortalSidebarProps } from './PortalSidebar';
import { PortalCommandPalette } from './PortalCommandPalette';

export interface PortalShellProps {
  customer: { kanzlei: string; email: string; tier: 'standard' | 'premium'; account_kind?: CustomerRecord['account_kind'] };
  matrixRoomId?: string;
  documentCount?: number | null;
  view: PortalView;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  onLogout: () => void;
  settingsPanel: React.ReactNode;
  children: React.ReactNode;
  sidebarProps?: Partial<Pick<PortalSidebarProps, 'onNavigate' | 'isPremium' | 'mehrExpanded' | 'onMehrExpandedChange'>>;
  onNavigate: (view: PortalView) => void;
  isPremium: boolean;
  contractGeneratorEnabled?: boolean;
  /** MEMCORE Modul B (Labor): „Mein Gedächtnis“ in der Sidebar. */
  memcoreEnabled?: boolean;
  teamAccessEnabled?: boolean;
  /** Optional: Fokus ARKO-Input bei `/` */
  onSlashFocus?: () => void;
  /** Chat/Editor: volle Höhe ohne äußeren Scroll */
  fillHeight?: boolean;
  /** Dokument+ARKO Arbeitsmodus: Sidebar/Mobile-Bar/Padding ausblenden */
  minimalChrome?: boolean;
}

export const PortalShell: React.FC<PortalShellProps> = ({
  customer,
  matrixRoomId,
  documentCount,
  view,
  mobileOpen,
  onMobileOpenChange,
  settingsOpen,
  onSettingsOpenChange,
  onLogout,
  settingsPanel,
  children,
  onNavigate,
  isPremium,
  contractGeneratorEnabled,
  memcoreEnabled,
  teamAccessEnabled = false,
  onSlashFocus,
  fillHeight = false,
  minimalChrome = false,
}) => {
  const [cmdkOpen, setCmdkOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdkOpen(true);
        return;
      }
      if (cmdkOpen) return;
      if (e.key === 'Escape') {
        if (settingsOpen) {
          onSettingsOpenChange(false);
          return;
        }
        if (mobileOpen) {
          onMobileOpenChange(false);
          return;
        }
        window.dispatchEvent(new CustomEvent('arkorbit:portal-escape'));
        return;
      }
      if (
        e.key === '/' &&
        !settingsOpen &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        !(document.activeElement as HTMLElement)?.isContentEditable
      ) {
        e.preventDefault();
        onSlashFocus?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmdkOpen, settingsOpen, mobileOpen, onSettingsOpenChange, onMobileOpenChange, onSlashFocus]);

  const handleNavigate = (v: PortalView) => {
    onNavigate(v);
    onMobileOpenChange(false);
  };

  return (
    <div className={`${PORTAL_SHELL.app}${minimalChrome ? ' portal-doc-work-mode-active' : ''}`}>
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          aria-label="Menü schließen"
          onClick={() => onMobileOpenChange(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 transform ${
          minimalChrome ? 'hidden' : mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:translate-x-0 transition-transform duration-200 ease-out`}
      >
        <PortalSidebar
          customer={customer}
          matrixRoomId={matrixRoomId}
          documentCount={documentCount}
          isPremium={isPremium}
          contractGeneratorEnabled={contractGeneratorEnabled}
          memcoreEnabled={memcoreEnabled}
          teamAccessEnabled={teamAccessEnabled}
          view={view}
          settingsOpen={settingsOpen}
          onNavigate={handleNavigate}
          onOpenSettings={() => {
            onSettingsOpenChange(true);
            onMobileOpenChange(false);
          }}
          onLogout={() => {
            onLogout();
            onMobileOpenChange(false);
          }}
        />
      </div>

      <main className={PORTAL_SHELL.mainArea}>
        {!minimalChrome && <div className={PORTAL_SHELL.topBorder} />}

        <div className={`${PORTAL_SHELL.mobileBar} ${minimalChrome ? 'hidden' : ''}`}>
          <button
            type="button"
            onClick={() => onMobileOpenChange(true)}
            className="text-slate-300 p-2 hover:bg-white/[0.06] rounded-lg"
            aria-label="Menü öffnen"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-white truncate flex-1 text-center px-2">
            {portalViewTitle(view)}
          </span>
          <span className="w-9 text-[10px] text-slate-600 truncate text-right">{customer.kanzlei}</span>
        </div>

        <div
          className={`relative ${
            fillHeight
              ? 'flex-1 min-h-0 flex flex-col overflow-hidden'
              : PORTAL_SHELL.mainScroll
          }`}
        >
          <ArkorbitGridBackground opacity={minimalChrome ? 0 : 0.035} />
          <div
            className={`${PORTAL_SHELL.mainContent} relative z-[1] ${
              minimalChrome ? '!p-0' : ''
            } ${fillHeight ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : ''}`}
          >
            {children}
          </div>
        </div>
      </main>

      {settingsOpen && (
        <>
          <button
            type="button"
            className={PORTAL_SHELL.settingsOverlay}
            aria-label="Einstellungen schließen"
            onClick={() => onSettingsOpenChange(false)}
          />
          <aside className={PORTAL_SHELL.settingsPanel} role="dialog" aria-label="Einstellungen">
            {settingsPanel}
          </aside>
        </>
      )}

      <PortalCommandPalette
        open={cmdkOpen}
        onOpenChange={setCmdkOpen}
        onNavigate={handleNavigate}
        onOpenSettings={() => {
          onSettingsOpenChange(true);
          onMobileOpenChange(false);
        }}
        onFocusArko={onSlashFocus}
      />
    </div>
  );
};
